import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { apiClient, handleApiError, WeatherReport } from '../utils/api';

interface ReportsContextType {
  reports: WeatherReport[];
  isLoading: boolean;
  error: string | null;
  refreshReports: () => Promise<void>;
  deleteReport: (reportId: string) => Promise<boolean>;
  addReport: (report: WeatherReport) => void;
  updateReport: (reportId: string, updatedReport: WeatherReport) => void;
}

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

interface ReportsProviderProps {
  children: ReactNode;
}

export const ReportsProvider: React.FC<ReportsProviderProps> = ({ children }) => {
  const [reports, setReports] = useState<WeatherReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.getReports(100);
      setReports(response.reports);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshReports = useCallback(async () => {
    await loadReports();
  }, [loadReports]);

  const deleteReport = useCallback(async (reportId: string): Promise<boolean> => {
    try {
      await apiClient.deleteReport(reportId);
      setReports(prev => prev.filter(report => report.reportId !== reportId));
      return true;
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      return false;
    }
  }, []);

  const addReport = useCallback((report: WeatherReport) => {
    setReports(prev => [report, ...prev]);
  }, []);

  const updateReport = useCallback((reportId: string, updatedReport: WeatherReport) => {
    setReports(prev => prev.map(report => 
      report.reportId === reportId ? updatedReport : report
    ));
  }, []);

  // Load reports on mount
  React.useEffect(() => {
    loadReports();
  }, [loadReports]);

  const value: ReportsContextType = {
    reports,
    isLoading,
    error,
    refreshReports,
    deleteReport,
    addReport,
    updateReport,
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};

export const useReportsContext = (): ReportsContextType => {
  const context = useContext(ReportsContext);
  if (context === undefined) {
    throw new Error('useReportsContext must be used within a ReportsProvider');
  }
  return context;
};